import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function() {
      const canvas = document.createElement('canvas');
      canvas.width = this.width;
      canvas.height = this.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(this, 0, 0);
      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};

// Helper function to create circular logo
const createCircularLogo = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = function() {
      const size = Math.max(this.width, this.height);
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      // Draw white circle background
      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.fill();

      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Draw image
      const offsetX = (size - this.width) / 2;
      const offsetY = (size - this.height) / 2;
      ctx.drawImage(this, offsetX, offsetY, this.width, this.height);

      try {
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = url;
  });
};

const addCompanyHeader = async (doc, primaryColor) => {
  // Company Header Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');

  // Try to add circular logo
  try {
    const circularLogo = await createCircularLogo('/assets/jle-logo.png');
    doc.addImage(circularLogo, 'PNG', 15, 8, 25, 25);
  } catch (error) {
    console.log('Logo not loaded, proceeding without logo');
  }

  // Company name and tagline
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('JLE TRUCKING, INC', 105, 20, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Professional Trucking Services', 105, 28, { align: 'center' });

  // Company contact information (right side of header)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('8826 West Flagler Street', 195, 14, { align: 'right' });
  doc.text('Miami, Florida 33174', 195, 20, { align: 'right' });
  doc.text('Phone: 561-283-6464', 195, 26, { align: 'right' });
};

export const generateInvoicePDF = async (invoice) => {
  const doc = new jsPDF();

  // Colors
  const primaryColor = [0, 51, 102]; // Dark blue
  const secondaryColor = [100, 100, 100]; // Gray

  // Add company header with logo
  await addCompanyHeader(doc, primaryColor);

  // Reset text color
  doc.setTextColor(0, 0, 0);

  // Invoice Title
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 20, 55);

  // Invoice Details
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Invoice #: ${invoice.invoiceNumber}`, 20, 65);
  doc.text(`Invoice Date: ${new Date(invoice.invoiceDate).toLocaleDateString()}`, 20, 71);
  if (invoice.dueDate) {
    doc.text(`Due Date: ${new Date(invoice.dueDate).toLocaleDateString()}`, 20, 77);
  }

  // Customer Information
  doc.setFont('helvetica', 'bold');
  doc.text('BILL TO:', 20, 90);
  doc.setFont('helvetica', 'normal');

  let yPos = 97;
  if (invoice.customerSnapshot.company) {
    doc.text(invoice.customerSnapshot.company, 20, yPos);
    yPos += 6;
  }
  doc.text(invoice.customerSnapshot.name, 20, yPos);
  yPos += 6;

  if (invoice.customerSnapshot.address) {
    const addr = invoice.customerSnapshot.address;
    if (addr.street) {
      doc.text(addr.street, 20, yPos);
      yPos += 6;
    }
    if (addr.city || addr.state || addr.zip) {
      const cityStateZip = `${addr.city || ''}${addr.city && addr.state ? ', ' : ''}${addr.state || ''} ${addr.zip || ''}`;
      doc.text(cityStateZip, 20, yPos);
      yPos += 6;
    }
  }

  if (invoice.customerSnapshot.phone) {
    doc.text(`Phone: ${invoice.customerSnapshot.phone}`, 20, yPos);
    yPos += 6;
  }
  if (invoice.customerSnapshot.email) {
    doc.text(`Email: ${invoice.customerSnapshot.email}`, 20, yPos);
  }

  // Loads Table
  const tableStartY = yPos + 15;

  // Get consignee name once for all rows
  const consigneeName = invoice.consigneeSnapshot
    ? (invoice.consigneeSnapshot.company || invoice.consigneeSnapshot.name)
    : '-';

  const tableData = invoice.loads.map((load) => {
    let unitsText = '';
    let unitPrice = '';

    // Handle new format with loadType
    if (load.loadType) {
      if (load.loadType === 'Pallets') {
        unitsText = load.quantity ? `${load.quantity} pallets` : '-';
        unitPrice = 'Flat Rate';
      } else if (load.loadType === 'Bulk') {
        unitsText = load.weight ? `${parseFloat(load.weight).toFixed(2)} tons` : '-';
        unitPrice = load.ratePerUnit ? `$${parseFloat(load.ratePerUnit).toFixed(2)}` : '-';
      } else if (load.loadType === 'Jumbo Bags') {
        unitsText = load.numberOfLoads ? `${load.numberOfLoads} loads` : '-';
        unitPrice = load.ratePerUnit ? `$${parseFloat(load.ratePerUnit).toFixed(2)}` : 'Flat Rate';
      }
    } else {
      // Handle old format without loadType (fallback)
      unitsText = load.weight ? `${load.weight} ${load.weightUnit || 'lbs'}` : '-';
      unitPrice = load.ratePerUnit ? `$${parseFloat(load.ratePerUnit).toFixed(2)}` :
                  (load.rate ? `$${parseFloat(load.rate).toFixed(2)}` : '-');
    }

    const row = [
      load.pickupDate ? new Date(load.pickupDate).toLocaleDateString() : '-',
      load.loadType || load.description,
      unitsText,
      unitPrice,
      consigneeName,
      load.bolNumber || '-',
      load.poNumber || '-',
      `$${load.rate.toFixed(2)}`
    ];
    return row;
  });

  doc.autoTable({
    startY: tableStartY,
    head: [['Date', 'Load Type', 'Units', 'Unit Price', 'Shipped To', 'BOL #', 'PO #', 'Amount']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    columnStyles: {
      0: { cellWidth: 40 },
      6: { halign: 'right' }
    }
  });

  // Totals section
  let totalsYPos = doc.lastAutoTable.finalY + 10;
  const totalsX = 140;

  // Show fuel surcharge if present
  if (invoice.fuelSurcharge > 0 && invoice.fuelSurchargeRate > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Fuel Surcharge (${invoice.fuelSurchargeRate}%):`, totalsX, totalsYPos);
    doc.text(`$${invoice.fuelSurcharge.toFixed(2)}`, 190, totalsYPos, { align: 'right' });
    totalsYPos += 8;
  }

  // Show total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('TOTAL:', totalsX, totalsYPos);
  doc.text(`$${invoice.total.toFixed(2)}`, 190, totalsYPos, { align: 'right' });

  const totalY = totalsYPos;

  // Notes
  if (invoice.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Notes:', 20, totalY + 15);
    const splitNotes = doc.splitTextToSize(invoice.notes, 170);
    doc.text(splitNotes, 20, totalY + 21);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(...secondaryColor);
  doc.text('Thank you for your business!', 105, 280, { align: 'center' });

  return doc;
};

export const downloadInvoicePDF = async (invoice) => {
  const doc = await generateInvoicePDF(invoice);
  doc.save(`Invoice-${invoice.invoiceNumber}.pdf`);
};

export const printInvoicePDF = async (invoice) => {
  const doc = await generateInvoicePDF(invoice);
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};
